import React from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import Reveal from "../components/Reveal";
import { PageHero } from "./Services";
import { industries, company } from "../data/mock";
import Seo from "../components/Seo";

const benefits = [
  "More qualified leads and phone calls",
  "Higher local search rankings",
  "Predictable, trackable growth",
  "A partner who understands your market",
];

const WhoWeHelp = () => {
  return (
    <div className="overflow-hidden">
      <Seo title="Who We Help — Local & Home-Service Businesses" description="Marketing built for plumbers, pool builders, HVAC, contractors, restaurants, local retail, and ambitious SMBs. Tailored to your industry and your goals." />
      <PageHero
        eyebrow="Who We Help"
        title="Marketing built for the businesses that build America"
        subtitle="From home-service pros to local favorites and ambitious founders — we tailor every strategy to your industry and your goals."
      />

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((ind, i) => (
              <Reveal key={ind.title} delay={i * 70}>
                <div className="group h-full rounded-2xl overflow-hidden border border-slate-200 bg-white card-lift">
                  <div className="relative h-52 overflow-hidden">
                    <img src={ind.image} alt={ind.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/20 to-transparent" />
                    <div className="absolute bottom-4 left-4 flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl brand-gradient shadow-lg">
                        <ind.icon className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="font-display text-xl font-bold text-white">{ind.title}</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-slate-600 leading-relaxed">{ind.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Value band */}
      <section className="section-pad bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">Why local businesses choose us</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">
              We speak your customers’ language
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              We’ve helped hundreds of businesses turn clicks into customers. We know how people search for your services — and how to make sure they find you first.
            </p>
            <div className="mt-8 space-y-4">
              {benefits.map((b) => (
                <div key={b} className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <span className="text-slate-700 font-medium">{b}</span>
                </div>
              ))}
            </div>
            <a href={company.bookingUrl} target="_blank" rel="noreferrer">
              <Button size="lg" className="mt-8 brand-gradient text-white font-bold group">
                See what we can do for you
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </Reveal>
          <Reveal delay={120} className="relative">
            <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-slate-200">
              <img src={industries[2].image} alt="Local business owner" className="w-full h-[440px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 glass rounded-2xl shadow-xl border border-white/60 px-6 py-5 animate-float">
              <p className="font-display text-3xl font-extrabold text-blue-600">100+</p>
              <p className="text-sm font-semibold text-slate-600">Local businesses served</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default WhoWeHelp;
